import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../../styles/common/ConfirmationModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faTruck,
  faTractor,
  faCar,
  faTools,
  faQuestion,
  faCamera,
  faFile,
  faHardHat,
  faCubes
} from '@fortawesome/free-solid-svg-icons';

/**
 * Componente para modal de confirmação de ações
 */
const ConfirmationModal = ({
  isOpen,
  visible,
  title,
  message,
  content,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  onClose,
  type = 'default', // default, danger, warning, success
  confirmButtonProps = {},
  isEquipamentoSelection = false,
  equipamentos = [],
  entradaId,
  allowMultipleSelection = false,
  selectionTitle = 'Selecione',  // Título para mostrar acima das opções
  showDetails = true, // Se deve mostrar detalhes como quantidade e horas
  showEquipamentoSelection = false,
  onConfirmWithEquipamento,
  items = [],
  showItemSelection = false,
  itemType = 'generic',
  onConfirmWithItems,
  selectionType = 'multiple', // 'single' ou 'multiple'
}) => {
  const [selectedEquipamento, setSelectedEquipamento] = useState(null);
  const [selectedEquipamentos, setSelectedEquipamentos] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  
  useEffect(() => {
    if (isOpen || visible) {
      setSelectedEquipamentos([]);
      setSelectedItems([]);
    }
  }, [isOpen, visible]);
  
  const shouldShow = isOpen || visible;
  
  if (!shouldShow) {
    return null;
  }

  const displayMessage = message || content;

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setSelectedEquipamento(null);
    setSelectedEquipamentos([]);
    setSelectedItems([]);
    if (onClose) {
      onClose();
    } else if (onCancel) {
      onCancel();
    }
  };
  
  const handleConfirmWithEquipamento = () => {
    if (onConfirmWithEquipamento) {
      onConfirmWithEquipamento(selectedEquipamentos);
    }
    handleClose();
  };
  
  const handleConfirmWithItems = () => {
    if (onConfirmWithItems) {
      onConfirmWithItems(selectedItems);
    }
    handleClose();
  };
  
  const toggleEquipamentoSelection = (equip) => {
    if (selectionType === 'single') {
      setSelectedEquipamentos([equip.id]);
    } else {
      setSelectedEquipamentos(prev => {
        const isSelected = prev.some(item => item.id === equip.id);
        if (isSelected) {
          return prev.filter(item => item.id !== equip.id);
        } else {
          return [...prev, equip];
        }
      });
    }
  };
  
  const toggleItemSelection = (itemId) => {
    if (selectionType === 'single') {
      setSelectedItems([itemId]);
    } else {
      setSelectedItems(prev => {
        const isSelected = prev.includes(itemId);
        return isSelected
          ? prev.filter(id => id !== itemId)
          : [...prev, itemId];
      });
    }
  };
  
  const getEquipamentoIconBasic = (nome) => {
    const lowerNome = nome.toLowerCase();
    if (lowerNome.includes('trator') || lowerNome.includes('escavadeira') || lowerNome.includes('retroescavadeira')) {
      return faTractor;
    } else if (lowerNome.includes('caminhão') || lowerNome.includes('caminhao')) {
      return faTruck;
    } else if (lowerNome.includes('carro') || lowerNome.includes('veículo') || lowerNome.includes('veiculo')) {
      return faCar;
    } else {
      return faTools;
    }
  };
  
  const getItemIcon = (item) => {
    if (!item || !item.type) return faQuestion;

    switch (item.type.toLowerCase()) {
      case 'photo':
      case 'foto':
        return faCamera;
      case 'document':
      case 'documento':
        return faFile;
      case 'worker':
      case 'mao_de_obra':
      case 'pessoa':
        return faHardHat;
      case 'equipment':
      case 'equipamento':
        return getEquipamentoIconBasic(item.nome || item.title || '');
      case 'material':
        return faCubes;
      default:
        return faQuestion;
    }
  };
  
  const getItemIconClass = (item) => {
    if (!item || !item.type) return '';

    switch (item.type.toLowerCase()) {
      case 'photo':
      case 'foto':
        return 'item-icon-photo';
      case 'document':
      case 'documento':
        return 'item-icon-document';
      case 'worker':
      case 'mao_de_obra':
      case 'pessoa':
        return 'item-icon-worker';
      case 'equipment':
      case 'equipamento':
        return 'item-icon-equipment';
      case 'material':
        return 'item-icon-material';
      default:
        return '';
    }
  };
  
  const getVariantIcon = () => {
    switch (type) {
      case 'danger':
        return faTimesCircle;
      case 'success':
        return faCheckCircle;
      case 'warning':
      default:
        return faExclamationTriangle;
    }
  };
  
  const renderEquipamentoSelection = () => {
    if (!isEquipamentoSelection || !equipamentos || equipamentos.length === 0) {
      return null;
    }
    
    return (
      <div className="equipamento-selection">
        <div className="selection-title">{selectionTitle}</div>
        
        {equipamentos.map((equip, idx) => (
          <div 
            key={idx} 
            className={`equipamento-selection-item ${
              allowMultipleSelection 
                ? selectedEquipamentos.some(item => item.id === equip.id) ? 'selected' : ''
                : selectedEquipamento === equip ? 'selected' : ''
            }`}
            onClick={() => {
              if (allowMultipleSelection) {
                toggleEquipamentoSelection(equip);
              } else {
                setSelectedEquipamento(equip);
              }
            }}
          >
            <div className="equipamento-selection-icon">
              <FontAwesomeIcon icon={getEquipamentoIconBasic(equip.nome || equip.titulo)} />
            </div>
            <div className="equipamento-selection-info">
              <div className="equipamento-selection-nome">{equip.nome || equip.titulo}</div>
              {showDetails && (equip.quantidade || equip.horasTrabalhadas) && (
                <div className="equipamento-selection-detalhes">
                  {equip.quantidade && (
                    <span>Qtd: {equip.quantidade || 1}</span>
                  )}
                  {equip.horasTrabalhadas && (
                    <span>Horas: {equip.horasTrabalhadas || 8}h</span>
                  )}
                </div>
              )}
              {equip.descricao && (
                <div className="equipamento-selection-descricao">
                  {equip.descricao.length > 40 
                    ? `${equip.descricao.substring(0, 40)}...` 
                    : equip.descricao}
                </div>
              )}
            </div>
            <div className="equipamento-selection-radio">
              {allowMultipleSelection ? (
                <input 
                  type="checkbox" 
                  name={`equipamento-${idx}`}
                  checked={selectedEquipamentos.some(item => item.id === equip.id)}
                  onChange={() => toggleEquipamentoSelection(equip)}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <input 
                  type="radio" 
                  name="equipamento" 
                  checked={selectedEquipamento === equip} 
                  onChange={() => setSelectedEquipamento(equip)}
                  onClick={e => e.stopPropagation()}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div className={`confirmation-modal ${isEquipamentoSelection ? 'wide-modal' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={`modal-header modal-header-${type}`}>
          <h3>{title}</h3>
          <button className="modal-close" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <p>{displayMessage}</p>
          {showEquipamentoSelection && renderEquipamentoSelection()}
          {showItemSelection && items && items.length > 0 && (
            <div className="item-selection">
              <div className="selection-title">
                Selecione {selectionType === 'single' ? 'o item' : 'os itens'}:
              </div>
              {items.map((item) => (
                <div
                  key={item.id}
                  className={`item-selection-item ${selectedItems.includes(item.id) ? 'selected' : ''}`}
                  onClick={() => toggleItemSelection(item.id)}
                >
                  <div className={`item-selection-icon ${getItemIconClass(item)}`}>
                    <FontAwesomeIcon icon={getItemIcon(item)} />
                  </div>
                  <div className="item-selection-info">
                    <div className="item-selection-title">{item.title || item.nome}</div>
                    <div className="item-selection-details">
                      {item.details && Object.entries(item.details).map(([key, value]) => (
                        <span key={key}>{key}: {value}</span>
                      ))}
                    </div>
                    {item.description && (
                      <div className="item-selection-description">{item.description}</div>
                    )}
                  </div>
                  <div className="item-selection-checkbox">
                    <input
                      type={selectionType === 'single' ? 'radio' : 'checkbox'}
                      checked={selectedItems.includes(item.id)}
                      onChange={() => {}}
                      name="item-selection"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button 
            className="btn-cancel" 
            onClick={handleClose}
          >
            {cancelText}
          </button>
          {showEquipamentoSelection ? (
            <button 
              className={`btn-confirm btn-${type}`} 
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmWithEquipamento();
              }}
              disabled={confirmButtonProps.loading || (isEquipamentoSelection && !selectedEquipamento && 
                (!allowMultipleSelection || selectedEquipamentos.length === 0))}
            >
              {confirmButtonProps.loading && <i className="fas fa-spinner fa-spin" style={{marginRight: '5px'}}></i>}
              {confirmText}
            </button>
          ) : showItemSelection ? (
            <button 
              className={`btn-confirm btn-${type}`} 
              onClick={(e) => {
                e.stopPropagation();
                handleConfirmWithItems();
              }}
              disabled={confirmButtonProps.loading || selectedItems.length === 0}
            >
              {confirmButtonProps.loading && <i className="fas fa-spinner fa-spin" style={{marginRight: '5px'}}></i>}
              {confirmText}
            </button>
          ) : (
            <button 
              className={`btn-confirm btn-${type}`} 
              onClick={(e) => {
                e.stopPropagation();
                onConfirm();
              }}
              disabled={confirmButtonProps.loading}
            >
              {confirmButtonProps.loading && <i className="fas fa-spinner fa-spin" style={{marginRight: '5px'}}></i>}
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool,
  visible: PropTypes.bool,
  title: PropTypes.string.isRequired,
  message: PropTypes.string,
  content: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  type: PropTypes.oneOf(['default', 'danger', 'warning', 'success']),
  confirmButtonProps: PropTypes.object,
  isEquipamentoSelection: PropTypes.bool,
  equipamentos: PropTypes.array,
  entradaId: PropTypes.string,
  allowMultipleSelection: PropTypes.bool,
  selectionTitle: PropTypes.string,
  showDetails: PropTypes.bool,
  showEquipamentoSelection: PropTypes.bool,
  onConfirmWithEquipamento: PropTypes.func,
  items: PropTypes.array,
  showItemSelection: PropTypes.bool,
  itemType: PropTypes.string,
  onConfirmWithItems: PropTypes.func,
  selectionType: PropTypes.string
};

ConfirmationModal.defaultProps = {
  isOpen: false,
  visible: false,
  confirmButtonProps: {},
  isEquipamentoSelection: false,
  equipamentos: [],
  allowMultipleSelection: false,
  selectionTitle: 'Selecione',
  showDetails: true,
  showEquipamentoSelection: false,
  selectionType: 'multiple'
};

export default ConfirmationModal; 